/**
 * Phase-2-Full Session 4 — client-side mirror of the server's
 * authoritative world state.
 *
 * Why this exists: the engine's render loop runs at 60+ Hz; the
 * Colyseus state patches land at 20 Hz. We can't render straight off
 * `room.state` — the snake positions would jump every 50 ms instead
 * of moving smoothly. We also can't trust each per-field change event
 * to fire in render-coherent batches; some patches update the head
 * before the segments and a mid-batch render would draw a snake
 * whose body lags its head by one tick.
 *
 * This module solves both problems by snapshotting the entire
 * authoritative world ONCE per state patch and exposing the result
 * with prev/curr buffers that the renderer interpolates between.
 *
 * Update model:
 *   - room.onStateChange fires whenever the server's patch arrives.
 *     At that moment we rotate `curr → prev` for every known entity
 *     and re-read the live state into `curr`. Any new entities are
 *     added; gone ones are deleted.
 *   - The renderer calls into this module from its draw pass and gets
 *     an interpolation alpha based on (Date.now() - patchReceivedAt)
 *     divided by the average inter-patch interval (~50ms). Clamped
 *     to [0, 1] so a delayed patch doesn't over-extrapolate.
 *
 * Phase-2-Lite's `remoteSnakes` map (snake_state relay) is now
 * deprecated for the live-mode rendering path — its data is a
 * subset of what's already in server state. We leave the relay
 * channel itself wired so a future "events only, no sim" experiment
 * could resurrect it cheaply, but the engine no longer subscribes.
 */

/** Snapshot of a server-side player, mirrored for the renderer.
 *  Includes prev/curr fields so the renderer can interpolate
 *  smoothly between server ticks. */
export interface ServerPlayer {
  // Identity (slow-changing — re-copied each patch but cheap).
  id: string;
  name: string;
  hue: number;
  pattern: string;
  isBot: boolean;
  /** Epoch-ms timestamp of the player's first join to this room.
   *  Used by the renderer to flag "newcomer" (< 60 s old) humans so
   *  other players have a visual signal to leave them alone. */
  joinedAt: number;

  // Current snapshot — authoritative as of last patch.
  alive: boolean;
  headX: number;
  headY: number;
  heading: number;
  mass: number;
  radius: number;
  boosting: boolean;
  invincibleUntil: number;
  segments: number[]; // flat [x0,y0,...] copied from ArraySchema

  // Previous snapshot — used for interpolation.
  prevHeadX: number;
  prevHeadY: number;
  prevHeading: number;
  prevMass: number;
  prevRadius: number;
  prevSegments: number[];

  // Patch timing.
  receivedAt: number;
  prevReceivedAt: number;
}

/** Lighter mirror for food — pellets either exist or they don't.
 *  We don't interpolate (static pellets) so no prev fields. */
export interface ServerFoodView {
  id: number;
  x: number;
  y: number;
  hue: number;
  mass: number;
  radius: number;
  /** Per-pellet animation seed for client-side breathing pulse. */
  phase: number;
}

/** Live snake registry — populated on patch arrival, consumed by
 *  RemoteSnakeRenderer's drawAll pass. Plain Map for fast iteration. */
export const serverPlayers = new Map<string, ServerPlayer>();

/** Live food list — populated on patch arrival. Plain Array because
 *  the renderer wants index-based access for frustum culling. */
export const serverFood: ServerFoodView[] = [];

/** Local session id, set when liveSession adopts a client. Used so
 *  the engine can find the local player's server-side entry. */
let localSessionIdRef: string | null = null;
export function setLocalSessionId(id: string | null): void {
  localSessionIdRef = id;
}
export function getLocalSessionId(): string | null {
  return localSessionIdRef;
}

/** Convenience — null when not yet synced. */
export function getLocalServerPlayer(): ServerPlayer | null {
  if (!localSessionIdRef) return null;
  return serverPlayers.get(localSessionIdRef) ?? null;
}

/**
 * Snapshot the server's room state into our local mirror. Called
 * from `liveSession` on every `onStateChange` patch.
 *
 * The caller passes the raw state object as `unknown` — we duck-type
 * it because the Colyseus types are heavy and we only read a few
 * fields. If a field is missing (mid-deploy schema mismatch) we just
 * keep the last-known value rather than crashing.
 */
export function applyServerStatePatch(state: unknown): void {
  const s = state as {
    players: { forEach: (cb: (v: unknown, k: string) => void) => void };
    food: ArrayLike<unknown>;
  };
  const now = Date.now();

  // ── Players ──
  const seen = new Set<string>();
  s.players.forEach((p, key) => {
    seen.add(key);
    const live = p as {
      sessionId: string; name: string; hue: number; pattern: string;
      isBot: boolean; alive: boolean;
      headX: number; headY: number; heading: number;
      mass: number; radius: number; boosting: boolean;
      invincibleUntil: number; joinedAt: number;
      segments: { length: number; [k: number]: number };
    };
    let entry = serverPlayers.get(key);
    if (entry) {
      // Rotate: current → previous.
      entry.prevHeadX     = entry.headX;
      entry.prevHeadY     = entry.headY;
      entry.prevHeading   = entry.heading;
      entry.prevMass      = entry.mass;
      entry.prevRadius    = entry.radius;
      entry.prevSegments  = entry.segments;
      entry.prevReceivedAt = entry.receivedAt;
    } else {
      entry = {
        id: key,
        name: '', hue: 200, pattern: 'plasma', isBot: false, joinedAt: 0,
        alive: true,
        headX: 0, headY: 0, heading: 0, mass: 0, radius: 0,
        boosting: false, invincibleUntil: 0, segments: [],
        prevHeadX: 0, prevHeadY: 0, prevHeading: 0,
        prevMass: 0, prevRadius: 0, prevSegments: [],
        receivedAt: now, prevReceivedAt: 0,
      };
      serverPlayers.set(key, entry);
    }
    entry.name = live.name;
    entry.hue = live.hue;
    entry.pattern = live.pattern;
    entry.isBot = live.isBot;
    entry.joinedAt = live.joinedAt;
    entry.alive = live.alive;
    entry.headX = live.headX;
    entry.headY = live.headY;
    entry.heading = live.heading;
    entry.mass = live.mass;
    entry.radius = live.radius;
    entry.boosting = live.boosting;
    entry.invincibleUntil = live.invincibleUntil;
    // Copy segments — ArraySchema is array-like so we drain it to a
    // plain array for the renderer. The slice is a perf hot-spot
    // worth watching but at ~30 segments × 150 entities = 4500 numbers
    // per patch, it's a sub-millisecond copy.
    const segLen = live.segments.length;
    const segOut: number[] = entry.segments === entry.prevSegments
      ? [] // first entry into this slot — don't share refs with prev
      : entry.segments;
    segOut.length = segLen;
    for (let i = 0; i < segLen; i++) segOut[i] = live.segments[i] ?? 0;
    entry.segments = segOut;
    entry.receivedAt = now;
  });
  // Sweep absent entries (player left + server reaped).
  for (const key of serverPlayers.keys()) {
    if (!seen.has(key)) serverPlayers.delete(key);
  }

  // ── Food ──
  // ArraySchema length is stable post-init (the server mutates in
  // place). We extend serverFood once and overwrite every patch.
  const foodLen = s.food.length;
  while (serverFood.length < foodLen) {
    serverFood.push({ id: 0, x: 0, y: 0, hue: 200, mass: 0, radius: 0, phase: Math.random() * Math.PI * 2 });
  }
  serverFood.length = foodLen;
  for (let i = 0; i < foodLen; i++) {
    const live = s.food[i] as {
      id: number; x: number; y: number; hue: number;
      mass: number; radius: number;
    };
    const out = serverFood[i]!;
    // If the pellet's id changed, it was respawned — reset its
    // animation phase so the pulse doesn't carry over the burst.
    if (out.id !== live.id) {
      out.phase = Math.random() * Math.PI * 2;
    }
    out.id = live.id;
    out.x = live.x;
    out.y = live.y;
    out.hue = live.hue;
    out.mass = live.mass;
    out.radius = live.radius;
  }
}

/** Wipe the mirror — called when the live session ends so a future
 *  re-join doesn't surface stale entries. */
export function clearServerWorld(): void {
  serverPlayers.clear();
  serverFood.length = 0;
  localSessionIdRef = null;
}
